import { type ReactNode, useEffect } from "react";
import { AuthProvider as OidcAuthProvider, useAuth as useOidcAuth } from "react-oidc-context";
import { useNavigate } from "react-router";

const cognitoAuthority = import.meta.env.VITE_COGNITO_AUTHORITY;
const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;

const oidcConfig = {
  authority: cognitoAuthority,
  client_id: clientId,
  redirect_uri: `${window.location.origin}/auth/callback`,
  post_logout_redirect_uri: window.location.origin,
  scope: "openid email profile",
  response_type: "code",
  automaticSilentRenew: true,
  loadUserInfo: true,
};

export function AuthProvider({ children }: { children: ReactNode }) {
  return <OidcAuthProvider {...oidcConfig}>{children}</OidcAuthProvider>;
}

export function useAuth() {
  return useOidcAuth();
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const auth = useOidcAuth();

  if (auth.isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (auth.error) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <p className="text-destructive">Authentication error: {auth.error.message}</p>
        <button
          onClick={() => {
            void auth.signinRedirect();
          }}
          className="text-primary underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    void auth.signinRedirect();
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-muted-foreground">Redirecting to login...</div>
      </div>
    );
  }

  return <>{children}</>;
}

export function AuthCallback() {
  const auth = useOidcAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (auth.isAuthenticated) {
      void navigate("/", { replace: true });
    }
  }, [auth.isAuthenticated, navigate]);

  if (auth.error) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <p className="text-destructive">Authentication failed: {auth.error.message}</p>
        <button
          onClick={() => {
            void auth.signinRedirect();
          }}
          className="text-primary underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="text-muted-foreground">Completing sign-in...</div>
    </div>
  );
}
